import { useMemo, useCallback } from "react";
import { Slot } from "@radix-ui/react-slot";

import { buttonVariant, type ButtonVariantProps } from "./button.variants";

export type UseButtonProps = ButtonVariantProps & {
  id?: string;
  className?: string;
  sectionName?: string;
  children: React.ReactNode;
  type?: "button" | "submit";
  asChild?: boolean;
  title?: string | null;
  loadingLabel?: string;
  isLoading?: boolean;
  disabled?: boolean;
  hide?: boolean;
  onMouseEnter?: React.MouseEventHandler<HTMLButtonElement | HTMLAnchorElement>;
  onMouseLeave?: React.MouseEventHandler<HTMLButtonElement | HTMLAnchorElement>;
  onClick?: React.MouseEventHandler<HTMLButtonElement | HTMLAnchorElement>;
  tabIndex?: number;
  "aria-expanded"?: boolean;
  "aria-haspopup"?: boolean;
};

export const useButton = (props: UseButtonProps) => {
  const {
    className = "",
    sectionName = "",
    children,
    title,
    appearance = "link",
    size = "md",
    color = "white",
    weight = "medium",
    disabled = false,
    rounded = false,
    fluid,
    isLoading = false,
    loadingLabel,
    onClick,
    hide = false,
    asChild = false,
    isIconOnly = false,
    ...componentProps
  } = props;

  const isDisabled = isLoading || disabled;

  const styles = useMemo(
    () =>
      buttonVariant({
        appearance,
        size,
        color,
        weight,
        rounded,
        fluid,
        isIconOnly,
      }),
    [appearance, size, color, weight, rounded, fluid, isIconOnly],
  );

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
      if (isDisabled) return;

      if (onClick) {
        onClick(e);
      }
    },
    [isDisabled, onClick],
  );

  const Component = asChild ? Slot : "button";

  return {
    children,
    className,
    title,
    loadingLabel,
    styles,
    hide,
    isLoading,
    disabled: isDisabled,
    Component,
    componentProps: {
      ...componentProps,
      onClick: handleClick,
    },
  };
};
